import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';

import { getRef } from './utils';
import styles from './Typography.less';

const cx = classnames.bind(styles);

/**
 * **Access:**
 * ```js
 * import {Typography} from '@iss/client-react-components'
 * ```
 *
 * The Typography component is the base building block component. It provides a central implementation of the
 * Type [style guide](https://issgovernance.invisionapp.com/d/main#/console/12853972/298575303/preview)
 * @class Typography
 */
export default class Typography extends PureComponent {
  /**
   * Provides a static highorder component to render Typography configured to render an H1 element with Heading1 styles.
   * @component
   * @public
   * @param {Object} props The component props
   * @returns {Component} A HOC wrapping Typography with Style Preconfigured
   */
  static Heading1 = React.forwardRef((props, ref) => <Typography as="h1" {...props} ref={ref} face="heading1" />);

  /**
   * Provides a static highorder component to render Typography configured to render an H2 element with Heading2 styles.
   * @component
   * @public
   * @param {Object} props The component props
   * @returns {Component} A HOC wrapping Typography with Style Preconfigured
   */
  static Heading2 = React.forwardRef((props, ref) => <Typography as="h2" {...props} ref={ref} face="heading2" />);

  /**
   * Provides a static highorder component to render Typography configured to render an H3 element with Heading3 styles.
   * @component
   * @public
   * @param {Object} props The component props
   * @returns {Component} A HOC wrapping Typography with Style Preconfigured
   */
  static Heading3 = React.forwardRef((props, ref) => <Typography as="h3" {...props} ref={ref} face="heading3" />);

  /**
   * Provides a static highorder component to render Typography configured to render an H4 element with Heading4 styles.
   * @component
   * @public
   * @param {Object} props The component props
   * @returns {Component} A HOC wrapping Typography with Style Preconfigured
   */
  static Heading4 = React.forwardRef((props, ref) => <Typography as="h4" {...props} ref={ref} face="heading4" />);

  /**
   * Provides a static highorder component to render Typography configured to render a block element with SubHeading styles.
   * @component
   * @public
   * @param {Object} props The component props
   * @returns {Component} A HOC wrapping Typography with Style Preconfigured
   */
  static SubHeading = React.forwardRef((props, ref) => <Typography as="div" {...props} ref={ref} face="subheading" />);

  /**
   * Provides a static highorder component to render Typography configured to render a block element with SubText1 styles.
   * @component
   * @public
   * @param {Object} props The component props
   * @returns {Component} A HOC wrapping Typography with Style Preconfigured
   */
  static SubText1 = React.forwardRef((props, ref) => <Typography as="div" {...props} ref={ref} face="subtext1" />);

  /**
   * Provides a static highorder component to render Typography configured to render a block element with SubText2 styles.
   * @component
   * @public
   * @param {Object} props The component props
   * @returns {Component} A HOC wrapping Typography with Style Preconfigured
   */
  static SubText2 = React.forwardRef((props, ref) => <Typography as="div" {...props} ref={ref} face="subtext2" />);

  /**
   * Provides a static highorder component to render Typography configured to render a P element with small styles.
   * @component
   * @public
   * @param {Object} props The component props
   * @returns {Component} A HOC wrapping Typography with Style Preconfigured
   */
  static Small = React.forwardRef((props, ref) => <Typography as="p" {...props} ref={ref} face="small" />);

  static propTypes = {
    /** Change the component used to render. */
    as: PropTypes.any.isRequired,

    /** Selects which style to use */
    face: PropTypes.oneOf([
      'paragraph',
      'small',
      'heading1',
      'heading2',
      'heading3',
      'heading4',
      'subheading',
      'subtext1',
      'subtext2',
    ]).isRequired,

    /** Use Serif font or Sans Serif */
    serif: PropTypes.bool,
  };

  static defaultProps = {
    as: 'span',
    face: 'paragraph',
    serif: false,
  };

  ref = React.createRef();

  getDOMNode() {
    return getRef(this);
  }

  render() {
    const { as: TagName, face, serif, className, disabled, ...props } = this.props;

    const cls = cx('typography', face, { serif }, className);

    return <TagName {...props} ref={this.ref} className={cls} disabled={disabled} />;
  }
}
